import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { API_URL } from "../../config/constants";
import socket from "../../socket";
import {
  ChatCard, ChatHeader, MessageList, Row, Avatar, Bubble, BubbleTime,
  SystemRow, SystemBubble,
  ProposalCard, ProposalTitle, ProposalText, ProposalActions, ProposalOutcome,
  AcceptButton, DeclineButton,
  EmptyMsg, InputRow, ChatInput, SendButton,
} from "./styles";

const IconSend = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const formatTime = (date) =>
  new Date(date).toLocaleString("en-GB", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });

// Desenlace de la propuesta, para rotularlo en la propia tarjeta.
// null mientras sigue pendiente de respuesta.
const OUTCOME_BY_STATUS = {
  scheduled: "accepted",
  in_progress: "accepted",
  completed: "accepted",
  declined: "declined",
  cancelled: "cancelled",
};

export const formatSlot = (date) =>
  new Date(date).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });

/**
 * Chat 1-a-1 entre el usuario autenticado y otro usuario.
 * Historial por REST y mensajes nuevos por socket, que es el contrato
 * que ya implementa el servidor (sendMessage / receiveMessage).
 */
const CaregiverChat = ({
  title, currentUserId, otherUserId, otherAvatar, ownAvatar,
  canRespondToProposals = false, onRespondProposal,
}) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [responding, setResponding] = useState(false);
  const listRef = useRef();
  const bottomRef = useRef();
  // El historial ya se abre abajo del todo: solo animar los mensajes nuevos.
  const didInitialScroll = useRef(false);

  useEffect(() => {
    if (!otherUserId) return;
    const token = localStorage.getItem("token");
    fetch(`${API_URL}/messages/${otherUserId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setMessages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [otherUserId]);

  useEffect(() => {
    const handleReceive = (msg) => {
      const belongsHere =
        (msg.sender_id === otherUserId && msg.receiver_id === currentUserId) ||
        (msg.sender_id === currentUserId && msg.receiver_id === otherUserId);
      if (!belongsHere) return;
      // El servidor confirma al emisor y notifica al receptor: evitar duplicados.
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    };

    // Al cambiar el estado de una cita, refrescar las tarjetas que la referencian.
    const handleSessionUpdated = (session) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.session_id === session.id ? { ...m, session_status: session.status } : m
        )
      );
    };

    // El servidor solo admite chat entre paciente y terapeuta asignado.
    const handleRejected = () => toast.error(t("caregiver.notAssigned"));

    socket.on("receiveMessage", handleReceive);
    socket.on("sessionUpdated", handleSessionUpdated);
    socket.on("messageRejected", handleRejected);
    return () => {
      socket.off("receiveMessage", handleReceive);
      socket.off("sessionUpdated", handleSessionUpdated);
      socket.off("messageRejected", handleRejected);
    };
  }, [currentUserId, otherUserId, t]);

  // Al cambiar de conversacion, volver a posicionar sin animacion.
  useEffect(() => {
    didInitialScroll.current = false;
  }, [otherUserId]);

  useEffect(() => {
    if (!messages.length) return;
    if (!didInitialScroll.current) {
      didInitialScroll.current = true;
      const list = listRef.current;
      if (list) list.scrollTop = list.scrollHeight;
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  const send = () => {
    const content = text.trim();
    if (!content || !otherUserId) return;
    socket.emit("sendMessage", { to: otherUserId, content });
    setText("");
  };

  const respond = async (sessionId, action) => {
    setResponding(true);
    try {
      await onRespondProposal(sessionId, action);
    } finally {
      setResponding(false);
    }
  };

  const renderMessage = (msg) => {
    const own = msg.sender_id === currentUserId;

    if (msg.type === "system") {
      return (
        <SystemRow key={msg.id}>
          <SystemBubble>
            {msg.content}
            <BubbleTime>{formatTime(msg.sent_at)}</BubbleTime>
          </SystemBubble>
        </SystemRow>
      );
    }

    if (msg.type === "consultation_proposal") {
      const when = msg.session_scheduled_at || msg.content;
      const pending = msg.session_status === "proposed";
      const outcome = OUTCOME_BY_STATUS[msg.session_status];
      return (
        <div key={msg.id}>
          <ProposalCard $own={own}>
            <ProposalTitle>📅 {t("caregiver.proposal.title")}</ProposalTitle>
            <ProposalText>
              {t("caregiver.proposal.body")} <strong>{formatSlot(when)}</strong>
            </ProposalText>

            {pending && canRespondToProposals && (
              <ProposalActions>
                <AcceptButton disabled={responding} onClick={() => respond(msg.session_id, "accept")}>
                  {t("caregiver.proposal.accept")}
                </AcceptButton>
                <DeclineButton disabled={responding} onClick={() => respond(msg.session_id, "decline")}>
                  {t("caregiver.proposal.decline")}
                </DeclineButton>
              </ProposalActions>
            )}

            {outcome && (
              <ProposalOutcome $outcome={outcome}>
                {outcome === "accepted" ? "✓" : "✕"} {t(`caregiver.proposal.outcome.${outcome}`)}
              </ProposalOutcome>
            )}

            <BubbleTime>{formatTime(msg.sent_at)}</BubbleTime>
          </ProposalCard>
        </div>
      );
    }

    return (
      <Row key={msg.id} $own={own}>
        {!own && otherAvatar && <Avatar src={otherAvatar} alt="" />}
        <Bubble $own={own}>
          {msg.content}
          <BubbleTime>{formatTime(msg.sent_at)}</BubbleTime>
        </Bubble>
        {own && ownAvatar && <Avatar src={ownAvatar} alt="" />}
      </Row>
    );
  };

  return (
    <ChatCard>
      <ChatHeader>{title}</ChatHeader>

      <MessageList ref={listRef}>
        {messages.length === 0 && <EmptyMsg>{t("caregiver.empty")}</EmptyMsg>}
        {messages.map(renderMessage)}
        <div ref={bottomRef} />
      </MessageList>

      <InputRow>
        <ChatInput
          placeholder={t("caregiver.placeholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          disabled={!otherUserId}
        />
        <SendButton onClick={send} disabled={!text.trim() || !otherUserId}>
          <IconSend />
          {t("caregiver.send")}
        </SendButton>
      </InputRow>
    </ChatCard>
  );
};

export default CaregiverChat;
